import { useEffect, useRef, useState } from 'react';
import { PROCESS_SLIDES } from '@/data/content.ts';
import { asset } from '@/lib/paths.ts';
import { Img } from './Img.tsx';

const FADE_MS = 280;
const AUTO_MS = 5000;

/**
 * Four-step process carousel.
 *
 * Preserved from the original, deliberately:
 *  - Auto-advance does not start until the carousel scrolls into view.
 *    Otherwise the timer runs invisibly from page load and the carousel is
 *    already several slides deep by the time a visitor reaches it.
 *  - Any manual interaction (arrow or dot) stops auto-advance permanently, so
 *    the slide never changes out from under someone reading it.
 *
 * Accessibility improvements over the original:
 *  - Wrapped in a labelled group with aria-live="polite" so slide changes are
 *    announced; the original swapped textContent silently.
 *  - Dots are real toggle buttons with aria-current and descriptive labels
 *    rather than unlabelled 3px-tall divs.
 */
export function ProcessCarousel() {
  const [current, setCurrent] = useState(0);
  const [fading, setFading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [started, setStarted] = useState(false);
  const [userTook, setUserTook] = useState(false);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const fadeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Preload all carousel slide images so transitions are instant with no blank flash.
  useEffect(() => {
    PROCESS_SLIDES.forEach((s) => {
      const img = new Image();
      img.src = asset(s.img);
    });
  }, []);

  // Gate autoplay on visibility and preserve .visible across React re-renders.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true);
      setStarted(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            setStarted(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const goTo = (index: number, fromUser: boolean) => {
    if (fromUser) setUserTook(true);
    setFading(true);
    if (fadeTimer.current) clearTimeout(fadeTimer.current);
    fadeTimer.current = setTimeout(() => {
      setCurrent((index + PROCESS_SLIDES.length) % PROCESS_SLIDES.length);
      setFading(false);
    }, FADE_MS);
  };

  // Auto-advance matching original behavior.
  useEffect(() => {
    if (!started || userTook) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => {
      setFading(true);
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
      fadeTimer.current = setTimeout(() => {
        setCurrent((c) => (c + 1) % PROCESS_SLIDES.length);
        setFading(false);
      }, FADE_MS);
    }, AUTO_MS);
    return () => clearInterval(id);
  }, [started, userTook]);

  useEffect(
    () => () => {
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
    },
    [],
  );

  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
    touchStartY.current = e.touches[0]?.clientY ?? null;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const deltaX = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    const deltaY = (e.changedTouches[0]?.clientY ?? 0) - touchStartY.current;
    // Horizontal swipe threshold: 40px and dominant over vertical scrolling
    if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
      if (deltaX < 0) {
        goTo(current + 1, true);
      } else {
        goTo(current - 1, true);
      }
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  const slide = PROCESS_SLIDES[current];
  if (!slide) return null;

  const renderArrows = (extraClass = '') => (
    <div className={`process-carousel__arrows ${extraClass}`.trim()}>
      <button
        type="button"
        className="process-carousel__arrow"
        aria-label="Previous step"
        onClick={() => goTo(current - 1, true)}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
      <button
        type="button"
        className="process-carousel__arrow"
        aria-label="Next step"
        onClick={() => goTo(current + 1, true)}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={`process-carousel fade-in ${visible ? 'visible' : ''} ${fading ? 'process-carousel--fading' : ''}`.trim()}
      role="group"
      aria-roledescription="carousel"
      aria-label="The Lux process, four steps"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="process-carousel__content">
        <div className="process-carousel__header">
          <h3 className="process-carousel__title">{slide.title}</h3>
          {renderArrows('process-carousel__arrows--desktop')}
        </div>

        {/* Politely announces the new step to screen readers on change. */}
        <p className="lead" aria-live="polite">
          {slide.desc}
        </p>

        <div className="process-carousel__controls">
          <div className="process-carousel__dots">
            {PROCESS_SLIDES.map((s, i) => (
              <button
                key={s.title}
                type="button"
                className={`process-carousel__dot ${
                  i === current ? 'process-carousel__dot--active' : ''
                }`.trim()}
                aria-label={`Step ${i + 1}: ${s.title}`}
                {...(i === current ? { 'aria-current': true as const } : {})}
                onClick={() => goTo(i, true)}
              />
            ))}
          </div>
          {renderArrows('process-carousel__arrows--mobile')}
        </div>
      </div>

      <div className="process-carousel__media">
        <Img
          src={slide.img}
          alt={slide.alt}
          width={1422}
          height={1067}
          priority
          sizes="(max-width: 900px) 100vw, 50vw"
        />
      </div>
    </div>
  );
}
